import { useState } from 'react';
import { MoreHorizontal, Pencil, Play, Pause, Trash2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Modal } from '@/components/modal';
import { cn } from '@/lib/utils';
import { useNotification } from '@/components/notifications';
import {
  useDeleteSchedule,
  usePauseSchedule,
  useResumeSchedule,
  useTriggerSchedule,
} from '@/hooks/mutations/schedules/use-schedule-mutations';
import { scheduleCadenceLabel } from '@/pages/workflow/component/controls/schedule-helpers';
import { isOneShot } from '@/services/workflow/schedule';
import type { OrgSchedule } from '@/types/schedules';

interface ScheduleCardProps {
  schedule: OrgSchedule;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDeleted?: () => void;
}

export function ScheduleCard({
  schedule,
  selected,
  onSelect,
  onEdit,
  onDeleted,
}: ScheduleCardProps) {
  const notification = useNotification();
  const pause = usePauseSchedule();
  const resume = useResumeSchedule();
  const trigger = useTriggerSchedule();
  const remove = useDeleteSchedule();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const args = { workflowId: schedule.workflow_id, scheduleId: schedule.id };
  // A one-shot has no recurrence to edit, pause, resume, or trigger early,
  // and the API rejects all four — so don't offer them.
  const oneShot = isOneShot(schedule);

  const handleToggle = async () => {
    try {
      if (schedule.enabled) {
        await pause.mutateAsync(args);
        notification.success('Schedule paused');
      } else {
        await resume.mutateAsync(args);
        notification.success('Schedule resumed');
      }
    } catch (err) {
      Sentry.captureException(err, { extra: { context: 'toggle-schedule' } });
      notification.error('Failed to update schedule');
    }
  };

  const handleTrigger = async () => {
    try {
      await trigger.mutateAsync(args);
      notification.success('Run triggered');
    } catch (err) {
      Sentry.captureException(err, { extra: { context: 'trigger-schedule' } });
      notification.error('Failed to trigger run');
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(args);
      notification.success(
        oneShot ? 'Scheduled run cancelled' : 'Schedule deleted',
      );
      setDeleteDialogOpen(false);
      onDeleted?.();
    } catch (err) {
      Sentry.captureException(err, { extra: { context: 'delete-schedule' } });
      notification.error(
        oneShot
          ? 'Failed to cancel scheduled run'
          : 'Failed to delete schedule',
      );
    }
  };

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent',
          selected && 'bg-accent',
        )}
      >
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium'>
            {schedule.name || schedule.workflow_name || 'Untitled schedule'}
          </p>
          <p className='truncate text-xs text-muted-foreground'>
            {scheduleCadenceLabel(schedule)}
          </p>
        </div>
        <span
          className={cn(
            'size-2 shrink-0 rounded-full',
            schedule.enabled ? 'bg-green-500' : 'bg-muted-foreground/40',
          )}
          aria-label={schedule.enabled ? 'Active' : 'Paused'}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant='ghost' size='icon' className='size-7 shrink-0'>
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' onClick={(e) => e.stopPropagation()}>
            {!oneShot && (
              <>
                <DropdownMenuItem onSelect={onEdit}>
                  <Pencil className='size-4' /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleToggle}>
                  {schedule.enabled ? (
                    <>
                      <Pause className='size-4' /> Pause
                    </>
                  ) : (
                    <>
                      <Play className='size-4' /> Resume
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleTrigger}>
                  <Play className='size-4' /> Trigger now
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onSelect={() => setDeleteDialogOpen(true)}>
              <Trash2 className='size-4' /> {oneShot ? 'Cancel' : 'Delete'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Modal
        title={oneShot ? 'Cancel scheduled run?' : 'Delete schedule?'}
        isDialogOpen={deleteDialogOpen}
        setIsDialogOpen={setDeleteDialogOpen}
        footer={
          <>
            {/* For a one-shot both controls would otherwise read as
                "cancel": the dismiss button says Cancel and the title asks
                whether to cancel the run, so dismissing and confirming look
                interchangeable. Name what each one actually does. */}
            <Button
              variant='outline'
              onClick={() => setDeleteDialogOpen(false)}
            >
              {oneShot ? 'Keep run' : 'Cancel'}
            </Button>
            <Button
              variant='destructive'
              onClick={() => void handleDelete()}
              disabled={remove.isPending}
            >
              {oneShot ? 'Cancel run' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className='text-sm text-muted-foreground'>
          &ldquo;{schedule.name || schedule.workflow_name || 'This schedule'}
          &rdquo;{' '}
          {oneShot
            ? 'will be cancelled and its pending run will never fire.'
            : 'will be permanently deleted and its future runs will stop.'}{' '}
          This cannot be undone.
        </p>
      </Modal>
    </>
  );
}
